/*!
 * serverClock.js — shared time base for tight cross-client timing
 * ---------------------------------------------------------------------------
 * The server owns the transport: startTimestamp (server.js, one transport
 * per project) is the moment
 * (in the SERVER's performance.now() space) at which score time 0 would be,
 * given the current speed multiplier. Any client that knows the offset between
 * its own performance.now() and the server's can turn a score time into a
 * local wall-clock time — and every client that does so lands on the same
 * instant, to within the offset error. That is what lets metronomes click in
 * phase on several machines without a dedicated beat broadcast.
 *
 * The offset is estimated NTP-style over the existing WebSocket:
 *   client  → { type:"time_ping", t0 }
 *   server  → { type:"time_pong", t0, serverNow, startTimestamp, speedMultiplier, isPlaying }
 *   offset  = serverNow − (t0 + t1) / 2        (t1 = local receipt time)
 * The sample with the smallest round trip is the most trustworthy, so the
 * estimate is the median offset of the three lowest-RTT samples in the
 * window. On a LAN this settles at a few ms; the round trip is exposed so
 * callers can judge the quality.
 *
 * DOM-free on purpose: the maths is unit-testable in node.
 */

const MAX_SAMPLES = 10;
const BURST_COUNT = 6;          // quick pings after (re)connect
const BURST_GAP_MS = 150;
const STEADY_GAP_MS = 4000;     // then keep the estimate fresh

const state = {
  samples: [],                  // { t0, t1, rtt, offset }
  offset: null,                 // serverNow − localNow  (ms)
  rtt: null,                    // rtt of the sample the estimate rests on
  transport: null,              // { startTimestamp, speedMultiplier, isPlaying }
  lastPongAt: 0,
  pings: 0,
  pongs: 0,
};

let _socket = null;
let _timer = null;

const now = () => performance.now();

/* ----------------------------------------------------------------------- */
/*  Estimate                                                               */
/* ----------------------------------------------------------------------- */
export function recordSample(t0, t1, serverNow) {
  const rtt = t1 - t0;
  if (!Number.isFinite(rtt) || rtt < 0 || !Number.isFinite(serverNow)) return;
  state.samples.push({ t0, t1, rtt, offset: serverNow - (t0 + t1) / 2 });
  if (state.samples.length > MAX_SAMPLES) state.samples.shift();
  recompute();
}

function recompute() {
  if (!state.samples.length) { state.offset = null; state.rtt = null; return; }
  const byRtt = [...state.samples].sort((a, b) => a.rtt - b.rtt);
  const best = byRtt.slice(0, Math.min(3, byRtt.length));
  const offsets = best.map(s => s.offset).sort((a, b) => a - b);
  state.offset = offsets[Math.floor(offsets.length / 2)];
  state.rtt = best[0].rtt;
}

export function isReady() { return state.offset != null; }

/** Server performance.now() estimate, or null before the first pong. */
export function serverNow(localNow = now()) {
  return state.offset == null ? null : localNow + state.offset;
}

/** Local performance.now() for a server-clock instant, or null. */
export function toLocal(serverMs) {
  return state.offset == null || !Number.isFinite(serverMs) ? null : serverMs - state.offset;
}

/* ----------------------------------------------------------------------- */
/*  Transport mapping                                                      */
/* ----------------------------------------------------------------------- */
export function updateTransport(t) {
  if (!t) return;
  state.transport = {
    startTimestamp: Number.isFinite(t.startTimestamp) ? t.startTimestamp : null,
    speedMultiplier: Number.isFinite(t.speedMultiplier) && t.speedMultiplier !== 0 ? t.speedMultiplier : 1,
    isPlaying: !!t.isPlaying,
  };
}


/**
 * Local wall time (performance.now() space) at which the shared transport
 * reaches `scoreMs`. null when the offset is unknown, the transport is
 * stopped, or playing backwards — callers fall back to their local clock.
 * Valid on every client for the same instant, so it is the right anchor
 * for anything that must happen together on several machines.
 */
export function scoreTimeToLocalWall(scoreMs) {
  const t = state.transport;
  if (state.offset == null || !t || !t.isPlaying || t.startTimestamp == null) return null;
  if (!(t.speedMultiplier > 0) || !Number.isFinite(scoreMs)) return null;
  const serverWall = t.startTimestamp + scoreMs / t.speedMultiplier;
  return serverWall - state.offset;
}

/** Current score time (ms) according to the shared transport, or null. */
export function transportScoreMs(localNow = now()) {
  const t = state.transport;
  if (state.offset == null || !t || !t.isPlaying || t.startTimestamp == null || !(t.speedMultiplier > 0)) return null;
  return (localNow + state.offset - t.startTimestamp) * t.speedMultiplier;
}

/* ----------------------------------------------------------------------- */
/*  Wire protocol                                                          */
/* ----------------------------------------------------------------------- */
export function onTimePong(data, localNow = now()) {
  if (!data || !Number.isFinite(data.t0) || !Number.isFinite(data.serverNow)) return;
  state.pongs++;
  state.lastPongAt = localNow;
  recordSample(data.t0, localNow, data.serverNow);
  if ("startTimestamp" in data || "isPlaying" in data) updateTransport(data);
}

function sendPing() {
  if (!_socket || _socket.readyState !== 1 /* OPEN */) return;
  state.pings++;
  try { _socket.send(JSON.stringify({ type: "time_ping", t0: now() })); } catch { /* ignore */ }
}

/** Start (or restart, on reconnect) the ping schedule on this socket. */
export function startServerClock(socket) {
  stopServerClock();
  _socket = socket;
  state.samples = []; state.offset = null; state.rtt = null;
  let sent = 0;
  const burst = () => {
    sendPing();
    sent++;
    _timer = setTimeout(sent < BURST_COUNT ? burst : steady, sent < BURST_COUNT ? BURST_GAP_MS : STEADY_GAP_MS);
  };
  const steady = () => { sendPing(); _timer = setTimeout(steady, STEADY_GAP_MS); };
  burst();
}

export function stopServerClock() {
  if (_timer) clearTimeout(_timer);
  _timer = null;
  _socket = null;
}

/** One-line status for console checks on two machines side by side. */
export function debugInfo() {
  const t = state.transport;
  return {
    ready: isReady(),
    offsetMs: state.offset == null ? null : Math.round(state.offset * 10) / 10,
    rttMs: state.rtt == null ? null : Math.round(state.rtt * 10) / 10,
    samples: state.samples.length,
    pings: state.pings,
    pongs: state.pongs,
    serverNow: serverNow() == null ? null : Math.round(serverNow()),
    transport: t ? { ...t } : null,
    transportScoreMs: transportScoreMs() == null ? null : Math.round(transportScoreMs()),
  };
}

if (typeof window !== "undefined") {
  window.serverClock = { isReady, serverNow, toLocal, scoreTimeToLocalWall, transportScoreMs, debugInfo };
}
